import { Component, OnInit } from '@angular/core';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import moment from 'moment';
import { saveAs } from '@progress/kendo-file-saver';
import { Papa } from 'ngx-papaparse';
import { TranslateService } from '@ngx-translate/core';
import { merge, cloneDeep } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { forkJoin } from 'rxjs';
import { RuleService } from 'src/app/core/service/rule.service';
import { Base64Util } from 'src/app/core/utility/base64-util';

@Component({
  standalone: false,
  selector: 'app-assessment-export',
  templateUrl: './assessment-export.component.html',
  styleUrls: ['./assessment-export.component.scss']
})
export class AssessmentExportComponent implements OnInit
{
  public listAssessmentVersions: Array<string>;
  public dialogOpened = false;
  public exportForm: any;
  public assessmentId: number;
  public assessmentVersion: string;
  public assessmentName: string;
  public maxDate: Date = moment().toDate();
  public exportFormats: Array<{ text: string; value: string }> = [
    { text: 'Assessments as Rows', value: '0' },
    { text: 'Responses as Rows', value: '1' },
    { text: 'SMC', value: '2' },
  ];
  public fields: FormlyFieldConfig[] = [];
  public dictionariesMap = new Map<number, Dictionary[]>();

  public columns: Array<string> = [
    'assessmentName',
    'version',
    'assessmentDate',
    'assessmentFulfilledBy',
    'assessmentFulfilledByOrganization',
    'internalId',
    'givenName',
    'familyName',
    'dob',
    'gender',
    'streetLine',
    'city',
    'postalCode',
    'state',
    'country',
    'entity',
    'method',
    'patientId',
    'patientIdentifiers',
    'assessmentId',
    'assessmentFulfillmentId',
    'caseId',
    'caseStatus',
    'workflowStatus',
    'channelId',
    'channelName',
    'channelCategory',
    'insurers'
  ];

  private textExported: string;
  private textYes: string;
  private textNo: string;
  private textAssessements: string;
  private textLookup: string;

  constructor(
    private fb: UntypedFormBuilder,
    private dictionaryService: DictionaryService,
    private assessmentService: AssessmentService,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private papa: Papa,
    private ruleService: RuleService) { }

  public get versions(): UntypedFormControl
  {
    return this.exportForm.get('versions');
  }

  public get start(): UntypedFormControl
  {
    return this.exportForm.get('start');
  }

  public get end(): UntypedFormControl
  {
    return this.exportForm.get('end');
  }

  public get exportLookup(): UntypedFormControl
  {
    return this.exportForm.get('exportLookup');
  }

  public get exportLabelForAnswer(): UntypedFormControl
  {
    return this.exportForm.get('exportLabelForAnswer');
  }

  public get exportDefaultEmptyValue(): UntypedFormControl
  {
    return this.exportForm.get('exportDefaultEmptyValue');
  }

  public get format(): UntypedFormControl
  {
    return this.exportForm.get('format');
  }

  public get exportColumns(): UntypedFormControl
  {
    return this.exportForm.get('exportColumns');
  }

  public get closeAfterExport(): UntypedFormControl
  {
    return this.exportForm.get('closeAfterExport');
  }

  public get externalProviderId(): UntypedFormControl
  {
    return this.exportForm.get('externalProviderId');
  }

  public get externalAssessmentId(): UntypedFormControl
  {
    return this.exportForm.get('externalAssessmentId');
  }

  public ngOnInit(): void
  {
    this.translate.get(['ASSESSMENT-EXPORTED-FILES', 'YES', 'NO', 'ASSESSMENTS', 'ASSESSMENTS-LOOKUP',
      'ASSESSMENT-EXPORT-FORMATS']).subscribe({
        next: (res) =>
        {
          this.textExported = res['ASSESSMENT-EXPORTED-FILES'];
          this.textYes = res.YES;
          this.textNo = res.NO;
          this.textAssessements = res.ASSESSMENTS;
          this.textLookup = res['ASSESSMENTS-LOOKUP'];

          // Set assessment export formats
          const splitted = res['ASSESSMENT-EXPORT-FORMATS'].split(',');
          for (let i = 0; i < splitted.length; i++)
          {
            this.exportFormats[i].text = splitted[i];
          }
        }
      });
  }

  /**
   * Execute method to open the export dialog.
   *
   * @param assessmentId Assessment Id.
   * @param assessmentVersion Assessement version to set the versions multi-select drop-down.
   * @param assessmentName Assessment name to show for which assessment it will export content.
   */
  public execute(assessmentId: number, assessmentVersion: string, assessmentName: string): void
  {
    this.assessmentId = assessmentId;
    this.assessmentVersion = assessmentVersion;
    this.assessmentName = assessmentName;
    this.assessmentService.getAssessmentVersions(assessmentId).subscribe({
      next: (versions) =>
      {
        this.listAssessmentVersions = versions;
        if (this.listAssessmentVersions.length === 0)
        {
          this.messageHandlerService.displayWarning('NO-ASSESSMENT-DATA-FOUND');
        }
        else
        {
          // Must read the assessment to get the externalId (if any) in replacement of the questionId
          this.assessmentService.getAssessment(assessmentId).subscribe({
            next: (data: any) =>
            {
              this.fields = this.assessmentService.getFlatAssessmentFields(JSON.parse(Base64Util.decodeUnicode(data.fields)));
              this.buildForm();
              this.dialogOpened = true;
            }
          });
        }
      }
    });
  }

  public close(): void
  {
    this.dialogOpened = false;
  }

  /**
   * Exporting assessments.
   *
   * @param e Event
   */
  public onExport(): void
  {
    // Perform start/end date validation
    if (this.start !== null && this.end !== null)
    {
      const d1 = moment(this.end.value);
      const d2 = moment(this.start.value);
      const diffMins = d1.diff(d2, 'minutes');
      if (diffMins <= 0)
      {
        this.messageHandlerService.displayError('DATE-RANGE-ERROR');
        return;
      }
    }
    // Export
    this.processAssesmentsExport();
  }

  public formatChange(format: any): void
  {
    if (format === undefined || !format)
    {
      this.localStorageService.clear('assessment-export-format');
    }
    else
    {
      this.localStorageService.store('assessment-export-format', format);
    }
    this.setControlsState();
  }

  public defaultEmptyValueChange(): void
  {
    this.localStorageService.store('assessment-export-default-empty-value', this.exportDefaultEmptyValue.value);
  }

  public columnsChange(columns: any): void
  {
    if (columns === undefined || !columns)
    {
      this.localStorageService.clear('assessment-export-columns');
    }
    else
    {
      this.localStorageService.store('assessment-export-columns', columns);
    }
  }

  /**
   * Create export form.
   */
  private buildForm(): void
  {
    let versions = [];

    // Auto-select if only one selection
    if (this.listAssessmentVersions.length === 1)
    {
      versions = this.listAssessmentVersions;
    }
    else if (this.listAssessmentVersions.findIndex(x => x === this.assessmentVersion) >= 0)
    {
      versions.push(this.assessmentVersion);
    }

    const storedFormat = this.localStorageService.retrieve('assessment-export-format');
    const exportColumns = this.localStorageService.retrieve('assessment-export-columns');
    const externalProviderId = this.localStorageService.retrieve('assessment-export-external-provider');
    const externalAssessmentId = this.localStorageService.retrieve('assessment-export-external-assessment');

    // Set form
    this.exportForm = this.fb.group({
      assessmentName: [{ value: this.assessmentName, disabled: true }],
      versions: [{ value: versions, disabled: false }, Validators.required],
      start: [{ value: null, disabled: false }, Validators.required],
      end: [{ value: this.maxDate, disabled: false }, Validators.required],
      format: [{ value: storedFormat !== null && storedFormat !== '' ? storedFormat : '0', disabled: false }, Validators.required],
      exportLookup: [{ value: false, disabled: false }],
      exportLabelForAnswer: [{ value: false, disabled: false }],
      exportDefaultEmptyValue: [{ value: this.localStorageService.retrieve('assessment-export-default-empty-value'), disabled: false }],
      closeAfterExport: [{ value: false, disabled: false }],
      exportColumns: [{
        value: exportColumns !== null && exportColumns !== '' ?
          exportColumns : null, disabled: false
      }],
      externalProviderId: [{
        value: externalProviderId !== null && externalProviderId !== '' ?
          externalProviderId : null, disabled: false
      }],
      externalAssessmentId: [{
        value: externalAssessmentId !== null && externalAssessmentId !== '' ?
          externalAssessmentId : null, disabled: false
      }],
    });

    this.exportForm.get('externalProviderId').valueChanges.subscribe({
      next: (provider: string) =>
      {
        this.externalProviderIdChange(provider);
      }
    });

    this.exportForm.get('externalAssessmentId').valueChanges.subscribe({
      next: (assessment: string) =>
      {
        this.externalAssessmentIdChange(assessment);
      }
    });

    this.externalProviderIdChange(this.externalProviderId.value);
    this.externalAssessmentIdChange(this.externalAssessmentId.value);
    this.setControlsState();
  }

  /**
   * Run the export based on selected criteria.
   * Export by creating one file per version.
   */
  private processAssesmentsExport(): void
  {
    let lookupDictionaries = undefined;
    this.dictionariesMap = new Map<number, Dictionary[]>();

    // All versions are executed using forkJoin and returns when all the data is retrieved
    this.assessmentService.getCompletedAssessmentsFromMultipleSources(
      this.versions.value, this.assessmentId, this.start.value, this.end.value).subscribe({
        next: (results) =>
        {
          // If export label for answer we must check if we have any codeset id
          if (this.exportLabelForAnswer.value) 
          {
            results.forEach(assessments => 
            {
              assessments.forEach(d =>
              {
                const transformedData = this.combineFormFieldsAndData(d.formData);
                if (transformedData)
                {
                  transformedData.forEach(data =>
                  {
                    if (data && data.templateOptions && data.templateOptions.codeSetId &&
                      this.dictionariesMap.get(data.templateOptions.codeSetId) === undefined)
                    {
                      if (lookupDictionaries === undefined) { lookupDictionaries = {}; }
                      lookupDictionaries[data.templateOptions.codeSetId] = this.dictionaryService.getCachedCodesByCodeSetId(data.templateOptions.codeSetId);
                    }
                  });
                }
              });
            });
          }

          // We must fetch the dictionaries first if exportLabelForAnswer.value
          if (lookupDictionaries !== undefined)
          {
            forkJoin(lookupDictionaries).subscribe({
              next: (responseList) =>
              {
                Object.keys(responseList).forEach((key: string) =>
                {
                  this.dictionariesMap.set(Number(key), responseList[key]);
                });
                this.exportAssessments(results);
              }
            });
          }
          else
          {
            this.exportAssessments(results);
          }
        }
      });
  }

  private exportAssessments(results: any): void
  {
    let exportedCount = 0; let transformedData = [];

    // Proceed exporting data per version
    results.forEach(assessments =>
    {
      if (assessments.length > 0)
      {
        const exported = [];
        const assessmentName = assessments[0].assessmentName;
        const assessmentVersion = assessments[0].version;

        // Prepare data to export
        assessments.forEach(d =>
        {
          // Transform UTC date to local date
          d.assessmentDate = moment(d.assessmentDate).format('YYYY-MM-DD');

          // Insert normalized form data with answers
          transformedData = this.combineFormFieldsAndData(d.formData);

          // Remove non selected columns
          if (this.exportColumns.value && this.exportColumns.value.length > 0)
          {
            this.columns.forEach(column =>
            {
              if (!this.exportColumns.value.includes(column))
              {
                delete d[column];
              }
            });
          }

          // Export based on the selected format
          if (this.format.value === '0')
          {
            // Merge form data generated for the current row
            d = merge(d, ...this.getNormalizedData(transformedData, true));

            // Process to override the value by the label or set the default value
            const keys = Object.keys(d);
            keys.forEach(key =>
            {
              const dataObj = transformedData.find(v => v.fieldKey === key);
              if (dataObj !== undefined)
              {
                if (this.exportLabelForAnswer.value && d[key] &&
                  (dataObj.templateOptions && dataObj.templateOptions.options && (dataObj.type === 'select' || dataObj.type === 'multiselect'))) 
                {
                  const listValues = d[key].split('|');
                  const newDisplayValues = [];
                  if (dataObj.templateOptions.codeSetId)
                  {
                    const dictionaryMap = this.dictionariesMap.get(dataObj.templateOptions.codeSetId);
                    if (dictionaryMap !== undefined)
                    {
                      listValues.forEach((lookupValue: string) =>
                      {
                        const field = dictionaryMap.find(x => x.code === lookupValue);
                        newDisplayValues.push(field ? field.displayName : lookupValue);
                      });
                      d[key] = newDisplayValues.join('|');
                    }
                  }
                  else
                  {
                    listValues.forEach((lookupValue: string) =>
                    {
                      const field = dataObj.templateOptions.options.find((x: any) => x.value === lookupValue);
                      newDisplayValues.push(field ? field.label : lookupValue);
                    });
                    d[key] = newDisplayValues.join('|');
                  }
                }
                // Set default if null
                if (d[key] === null)
                {
                  d[key] = this.exportDefaultEmptyValue.value;
                }
              }
            });

            exported.push(d);
            delete d.formData;  // Remove the raw form content from export
          }
          else
          {
            const normalizedData = this.getNormalizedData(transformedData, true);
            normalizedData.forEach(fieldData =>
            {
              const exportedData = cloneDeep(d);
              exportedData.questionId = Object.keys(fieldData)[0];
              exportedData.answerId = Object.values(fieldData)[0];

              // Process to override the value by the label or set the default value
              const dataObj = transformedData.find(v => v.fieldKey === exportedData.questionId);
              if (dataObj !== undefined)
              {
                if (this.exportLabelForAnswer.value && exportedData.answerId &&
                  (dataObj.templateOptions && dataObj.templateOptions.options && (dataObj.type === 'select' || dataObj.type === 'multiselect'))) 
                {
                  const listValues = exportedData.answerId.split('|');
                  const newDisplayValues = [];
                  if (dataObj.templateOptions.codeSetId)
                  {
                    const dictionaryMap = this.dictionariesMap.get(dataObj.templateOptions.codeSetId);
                    if (dictionaryMap !== undefined)
                    {
                      listValues.forEach((lookupValue: string) =>
                      {
                        const field = dictionaryMap.find(x => x.code === lookupValue);
                        newDisplayValues.push(field ? field.displayName : lookupValue);
                      });
                      exportedData.answerId = newDisplayValues.join('|');
                    }
                  }
                  else
                  {
                    listValues.forEach((lookupValue: string) =>
                    {
                      const field = dataObj.templateOptions.options.find((x: any) => x.value === lookupValue);
                      newDisplayValues.push(field ? field.label : lookupValue);
                    });
                    exportedData.answerId = newDisplayValues.join('|');
                  }
                }
                if (exportedData.answerId === null)
                {
                  exportedData.answerId = this.exportDefaultEmptyValue.value;
                }
              }

              // Is SMC custom format?
              if (this.format.value === '2')
              {
                this.formatSMC(exportedData);
              }
              delete exportedData.formData;   // Remove the raw form content from export
              exported.push(exportedData);
            });
          }
        });

        // This format is exporting as columns and the same columns must be part of the exported object.
        // This can occurs if the assessment form was changed overtime instead of the creation of another assessment version.
        if (this.format.value === '0')
        {
          // Get all columns from exported content
          const allColumns = [];
          exported.forEach(exportData =>
          {
            Object.keys(exportData).forEach((columnName) =>
            {
              if (allColumns.indexOf(columnName) === -1) { allColumns.push(columnName); }
            });
          });
          // Add any missing columns if (any)
          exported.forEach(exportData =>
          {
            allColumns.forEach((columnName) =>
            {
              if (exportData[columnName] === undefined) { exportData[columnName] = ''; }
            });
          });
        }

        // Set csv content
        const csvContent = this.papa.unparse(exported);

        // Set the default file name to the assessment name and version
        let now = moment().format('YYYYMMDD_hhmmss');
        let csvFileName = '';
        if (this.format.value === '2')
        {
          csvFileName = `${this.textAssessements}-${assessmentName}-${assessmentVersion}-${now}`;
        }
        else
        {
          const start = moment(this.start.value).format('YYYYMMDD_hhmmss');
          csvFileName = `${this.textAssessements}-${assessmentName}-${assessmentVersion}-${start}-${now}`;
        }

        // Override CSV filename for SMC
        if (this.format.value === '2')
        {
          now = moment().format('YYYYMMDD');
          csvFileName = `EvaluationImport.${this.externalProviderId.value}.${now}`;
        }

        const csvFileNameLookup = `${csvFileName}-${this.textLookup}`;

        // Create a blob object
        const BOM = '\uFEFF';
        const csvData = `${BOM}${csvContent}`;
        const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
        saveAs(URL.createObjectURL(blob), `${csvFileName}.csv`);

        // Export Assessments Lookups?
        this.exportAssessmentsLookup(transformedData, csvFileNameLookup);

        exportedCount++;
      }
    });

    // Show export results
    if (exportedCount === 0)
    {
      this.messageHandlerService.displayWarning('NO-ASSESSEMENTS-TO-EXPORT');
    }
    else
    {
      this.messageHandlerService.displayDirectMessage('success', this.textExported.replace('{{value}}', exportedCount.toString()));
      this.dialogOpened = !this.closeAfterExport.value;
    }
  }

  /**
   * Export Assessments Lookup
   *
   * @param transformedData Form data.
   */
  private exportAssessmentsLookup(transformedData: any, csvFileName: string): void
  {
    if (transformedData.length > 0 && this.exportLookup.value)
    {
      const legendData = this.getNormalizedData(transformedData);
      const keys = [];

      let csvContent = '';

      legendData.forEach(x =>
      {
        const key = Object.keys(x)[0];
        if (keys.indexOf(key) === -1)
        {
          // Must replace , by blank in the legend to prevent moving column
          csvContent += `${Object.keys(x)[0]}=${Object.values(x)[0].toString().replace(/,/g, '')}\n`;

          // Adding options to legend
          const dataObj = transformedData.find(v => v.fieldKey === key);
          if (dataObj !== undefined &&
            dataObj.templateOptions !== undefined &&
            dataObj.templateOptions.options !== undefined)
          {
            dataObj.templateOptions.options.forEach(o =>
            {
              csvContent += `${Object.keys(x)[0]}.${o.value}=${o.label.replace(/,/g, '')}\n`;
            });
          }
          csvContent += '\n';
          keys.push(key);
        }
      });

      const BOM = '\uFEFF';
      const csvData = `${BOM}${csvContent}`;
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      saveAs(URL.createObjectURL(blob), `${csvFileName}.csv`);
    }
  }

  /**
   * Normalized form fields and corresponding answers (if found) to an array or object.
   *
   * @param based64FormContent Assessment formly fields and data.
   */
  private combineFormFieldsAndData(based64FormContent: any): any
  {
    // Indicate if the default value is null
    const DEFAULT_EMPTY_VALUE = null;

    // Based64 decode of the form content
    const formContent = JSON.parse(Base64Util.decodeUnicode(based64FormContent));
    const flatFields = this.assessmentService.getFlatAssessmentFields(formContent.fields);
    formContent.fields = flatFields;

    // Extract data model
    const formDataModel = formContent.data;

    // Filter form data and exclude templates (for template type is undefined)
    const filteredFormDataFields = formContent.fields
      .filter(x => x.type !== undefined && x.type !== 'label')
      .map(x => ({
        type: x.type,
        templateOptions: x.templateOptions ? x.templateOptions : null,
        fieldKey: x.key,
        fieldKeySub: null,
        fieldLabel: x.templateOptions ? x.templateOptions.label : ''
      }));

    // For multicheckbox, must create one field for each options
    for (let i = 0; i < filteredFormDataFields.length; i++)
    {
      if (filteredFormDataFields[i].type === 'multicheckbox')
      {
        const options = filteredFormDataFields[i].templateOptions.options.map(o => ({
          type: filteredFormDataFields[i].type,
          templateOptions: filteredFormDataFields[i].templateOptions,
          fieldKey: filteredFormDataFields[i].fieldKey,
          fieldKeyValue: o.value,
          fieldLabel: filteredFormDataFields[i].fieldLabel
        }));
        filteredFormDataFields.splice(i, 1, ...options);  // Replace current entry and replaced with multicheckbox options
        i += options.length;                              // Increased the count with the added options to skip them
      }
    }

    // Adding responses
    if (formDataModel !== undefined)
    {
      // Must flaten fromDataModel
      const formFlatDataModel = [];
      this.getFormDataModelRecursively(formDataModel, formFlatDataModel);
      
      filteredFormDataFields.forEach(sourceField =>
      {
        if (sourceField.type === 'multicheckbox')
        {
          // Retrieve the data value if any. If not answered set to null
          const dataValue = formFlatDataModel[`${sourceField.fieldKey}.${sourceField.fieldKeyValue}`] !== undefined &&
            formFlatDataModel[`${sourceField.fieldKey}.${sourceField.fieldKeyValue}`] !== null ?
            formFlatDataModel[`${sourceField.fieldKey}.${sourceField.fieldKeyValue}`] : DEFAULT_EMPTY_VALUE;

          // Retrieve the label from the template options using the fieldKeyValue
          const optionLookup = sourceField.templateOptions.options.find(o => o.value === sourceField.fieldKeyValue);
          sourceField = merge(sourceField, {
            dataValue: dataValue ? (dataValue ? this.textYes : this.textNo) : this.textNo,
            dataLabel: optionLookup !== undefined ? optionLookup.label : null
          });
        }
        else
        {
          const dataModel = formFlatDataModel[sourceField.fieldKey];
          const dataValue = dataModel !== undefined && dataModel !== null && dataModel.value !== undefined ? dataModel.value : dataModel;
          const dataLabel = dataModel !== undefined && dataModel !== null && dataModel.label !== undefined ? dataModel.label : null;
          const source: any = {};
          source.dataValue = dataValue !== undefined ? dataValue : DEFAULT_EMPTY_VALUE;
          source.dataLabel = dataLabel;

          if (sourceField.type === 'checkbox' && source.dataValue !== '')
          {
            source.dataValue = source.dataValue ? this.textYes : this.textNo;
          }
          // Keep date value
          else if (sourceField.type === 'date' && source.dataValue !== null && source.dataValue.indexOf('T') === 10)
          {
            source.dataValue = source.dataValue.substring(0, 10);
          }
          else if (sourceField.type === 'datetime' && source.dataValue !== null && source.dataValue.indexOf('T') === 10)
          {
            source.dataValue = this.ruleService.convertUTCDateToLocalDate(new Date(source.dataValue)).toISOString().replace('Z', '').replace('T', ' ');
          }
          sourceField = merge(sourceField, source);
        }
      });
    }
    return filteredFormDataFields;
  }

  /**
   * Retrieve a key/value pair array of the data to export.
   *
   * @param formFields Transformed data fields/.
   * @param returnValue Indicates if we want to return the value (for the export) or label (for the legend) content.
   * @returns array Returns the key/value array of object.
   */
  private getNormalizedData(formFields: any, returnValue = false): any
  {
    return formFields.map(x =>
    {
      // Sub-key is used to represent multicheckbox, otherwise it is null
      const subKey = x.fieldKeyValue !== undefined ? `.${x.fieldKeyValue}` : '';
      // When returning label, don't use subkey and duplicates will be removed
      const key = returnValue ? `${x.fieldKey}${subKey}` : x.fieldKey;
      const value = returnValue ? x.dataValue : x.fieldLabel;
      // Set key value object
      const field = {};
      field[key] = value;
      return field;
    });
  }

  private setControlsState(): void
  {
    // Disable export columns for SMC format
    if (this.format.value === '2')
    {
      this.exportColumns.disable();
      this.exportColumns.setValue(null);
      this.exportForm.get('externalProviderId').setValidators([Validators.required]);
      this.exportForm.get('externalAssessmentId').setValidators([Validators.required]);
    }
    else
    {
      this.exportColumns.enable();
      this.exportForm.get('externalProviderId').setValidators([]);
      this.exportForm.get('externalAssessmentId').setValidators([]);
    }
    this.exportForm.get('externalProviderId').updateValueAndValidity();
    this.exportForm.get('externalAssessmentId').updateValueAndValidity();
  }

  private externalProviderIdChange(provider: string): void
  {
    if (provider === undefined || !provider)
    {
      this.localStorageService.clear('assessment-export-external-provider');
    }
    else
    {
      this.localStorageService.store('assessment-export-external-provider', provider);
    }
  }

  private externalAssessmentIdChange(assessment: string): void
  {
    if (assessment === undefined || !assessment)
    {
      this.localStorageService.clear('assessment-export-external-assessment');
    }
    else
    {
      this.localStorageService.store('assessment-export-external-assessment', assessment);
    }
  }

  /**
   * This method is taking the normalized format and transform to the SMC custom format.
   *
   * @param exportedData
   */
  private formatSMC(exportedData: any): void
  {
    exportedData['Provider ID'] = this.externalProviderId.value;
    exportedData['Member ID'] = exportedData.patientIdentifiers;
    exportedData['First Name'] = exportedData.givenName;
    exportedData['Last Name'] = exportedData.familyName;
    exportedData.DOB = exportedData.dob;
    exportedData.Gender = exportedData.gender;
    exportedData['Member Assessment ID'] = exportedData.assessmentFulfillmentId;
    exportedData['Assessment ID'] = this.externalAssessmentId.value;
    exportedData['Assessment Date'] = exportedData.assessmentDate;
    exportedData['Question ID'] = exportedData.questionId;
    exportedData.Answer = exportedData.answerId;
    exportedData.Note = '';  // Not used

    // Get the Answer mapping from the externalId set in the assessment
    const questionArray = exportedData.questionId.split('.');
    const fieldName = questionArray.length === 2 ? questionArray[0] : exportedData.questionId;
    const field = questionArray.length === 2 ? questionArray[1] : null;
    const foundField: any = this.fields.find(x => x.key === fieldName);

    // Override the question id from externalId
    if (foundField !== undefined && foundField.templateOptions)
    {
      if (foundField.templateOptions.externalId)
      {
        exportedData['Question ID'] = foundField.templateOptions.externalId;
      }
      // Override question id from options if set
      if (foundField.templateOptions && foundField.templateOptions.options && field !== null)
      {
        foundField.templateOptions.options.forEach((option: any) =>
        {
          if (option.externalId && field && option.value === field)
          {
            exportedData['Question ID'] = option.externalId;
          }
        });
      }
    }

    // Set member id
    exportedData['Member ID'] = null;
    if (exportedData.patientIdentifiers)
    {
      const identifierSearchCodes = ['MA', 'MC', 'PT'];
      identifierSearchCodes.forEach((identifierCode) =>
      {
        if (exportedData['Member ID'] === null)
        {
          const foundIdentifier = this.searchIdentifier(exportedData.patientIdentifiers, identifierCode);
          if (foundIdentifier)
          {
            exportedData['Member ID'] = foundIdentifier;
          }
        }
      });
    }

    // Delete all unnecessary standard fields
    delete exportedData.patientIdentifiers;
    delete exportedData.givenName;
    delete exportedData.familyName;
    delete exportedData.dob;
    delete exportedData.gender;
    delete exportedData.assessmentName;
    delete exportedData.version;
    delete exportedData.assessmentDate;
    delete exportedData.questionId;
    delete exportedData.answerId;
    delete exportedData.assessmentFulfilledBy;
    delete exportedData.assessmentFulfilledByOrganization;
    delete exportedData.internalId;
    delete exportedData.streetLine;
    delete exportedData.city;
    delete exportedData.postalCode;
    delete exportedData.state;
    delete exportedData.country;
    delete exportedData.entity;
    delete exportedData.method;
    delete exportedData.patientId;
    delete exportedData.assessmentId;
    delete exportedData.assessmentFulfillmentId;
    delete exportedData.caseStatus;
    delete exportedData.workflowStatus;
    delete exportedData.caseId;
    delete exportedData.channelId;
    delete exportedData.channelName;
    delete exportedData.channelCategory;
    delete exportedData.insurers;
  }

  /**
   * Method that search for specific identifier in the identifiers list.
   *
   * @param patientIdentifiers Patient identifiers
   * @param searchIdentifier   Specific identifier to search for. MC=Medicate, MA=Medicaid or PT=Extenal
   * @returns Null or identifier value if found
   */
  private searchIdentifier(patientIdentifiers: string, searchIdentifier: string): string
  {
    let foundIdentifier = null;
    const identifiers = patientIdentifiers.split('|');
    identifiers.forEach(identifier =>
    {
      if (foundIdentifier === null)
      {
        const arrayIdentifier = identifier.split('^');
        if (arrayIdentifier.length >= 2 && arrayIdentifier[0] === searchIdentifier)
        {
          foundIdentifier = arrayIdentifier[1];
        }
      }
    });
    return foundIdentifier;
  }

  private getFormDataModelRecursively(formDataModel: any, formFlatDataModel: any): void 
  {
    for (const key in formDataModel)
    {
      if (Array.isArray(formDataModel[key])) 
      {
        formFlatDataModel[key] = formDataModel[key].join('|');
      }
      else
        if (typeof formDataModel[key] === 'object') 
        {
          for (const subKey in formDataModel[key])
          {
            if (typeof formDataModel[key][subKey] === 'object')
            {
              this.getFormDataModelRecursively(formDataModel[key][subKey], formFlatDataModel);
            }
            else
            {
              formFlatDataModel[`${key}.${subKey}`] = formDataModel[key][subKey];
            }
          }
        }
        else
        {
          formFlatDataModel[key] = formDataModel[key];
        }
    }
  }
}
